/**
 * A standalone scene's own picture map (spec 030): the map of one scene,
 * keyed `scene` (D-3), and the six story picture operations (026, 027)
 * wrapped for a scene document through a one-scene story (D-4). One
 * implementation serves both owners, so they cannot drift. Pure; shared by
 * the editor store, the scene assistant and the Service.
 */
import type { PictureMap, StoredPictureMap } from '../@types';
import { syncPictureMap, type PictureScene } from '../pictures/pictureMap';
import { picturesSection } from '../storyOperations/describeStory';
import { storedPictureMapFrom } from '../storyOperations/document';
import {
  clearPictures, fillPictureSlot, notePictureSlot, removePictureSlot, resetPictureSlot, setPictureFraming, setPictureKind, setPicturePlayback,
} from '../storyOperations/pictureOperations';
import type { StoryDocument, StoryOperation } from '../storyOperations/types';
import type { OperationError, SceneDocument, SceneOperation } from './types';

/** The scene key of every use in a scene's own map (D-3). */
export const SCENE_PICTURE_KEY = 'scene';

/** The fields of a scene document the map reads. */
type PictureSceneDoc = Pick<SceneDocument, 'name'> & PictureScene['doc'] & { pictureMap?: StoredPictureMap | null };

/** The scene as the map reads it: one `PictureScene` keyed `scene`. */
export const scenePictureSceneOf = (doc: PictureSceneDoc): PictureScene => ({ key: SCENE_PICTURE_KEY, name: doc.name ?? '', doc });

/** The scene's derived picture map (026's rules over the one scene); no `pictureMap` key reads as `{slots: []}`. */
export function scenePictureMapOf(doc: PictureSceneDoc): PictureMap {
  return syncPictureMap(storedPictureMapFrom(doc.pictureMap ?? { slots: [] }), [scenePictureSceneOf(doc)]);
}

/** A one-scene story document around `doc` (D-4): the story picture operations run on it unchanged. */
export function pictureStoryOf(doc: SceneDocument): StoryDocument {
  const name = doc.name ?? '';
  return {
    name,
    startNodeId: '',
    nodes: [],
    edges: [],
    scenes: [{ key: SCENE_PICTURE_KEY, mode: 'owned', name, doc }],
    pictureMap: storedPictureMapFrom(doc.pictureMap ?? { slots: [] }),
  };
}

/**
 * A story picture operation as a scene operation (D-4): the same name and
 * `inputSchema`, its description with "story" read as "scene"; it applies the
 * story operation to `pictureStoryOf(doc)` (messages say "in this scene") and
 * returns that scene's document with `pictureMap` set to the result's map.
 */
export function scenePictureOperation<Args>(op: StoryOperation<Args>): SceneOperation<Args> {
  return {
    name: op.name,
    description: op.description.replace(/\bstory\b/g, 'scene').replace(/\bStory\b/g, 'Scene'),
    inputSchema: op.inputSchema,
    apply(doc, args, ctx) {
      const result = op.apply(pictureStoryOf(doc), args, { ...ctx, pictureNoun: 'scene' });
      if (result.ok === false) return { ok: false, errors: result.errors as OperationError[] };
      const scene = result.doc.scenes.find((s) => s.key === SCENE_PICTURE_KEY);
      const next: SceneDocument = { ...(scene ? scene.doc : doc), pictureMap: storedPictureMapFrom(result.doc.pictureMap) };
      return { ok: true, doc: next, summary: result.summary, createdIds: result.createdIds };
    },
  };
}

/** The picture operations for a scene, in `STORY_OPERATIONS` order (026's, 027's, 035's, then 026's `setPictureKind`). Never part of `SCENE_OPERATIONS` (D-4). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- each entry has its own Args
export const SCENE_PICTURE_OPERATIONS: readonly SceneOperation<any>[] = Object.freeze([
  scenePictureOperation(notePictureSlot),
  scenePictureOperation(removePictureSlot),
  scenePictureOperation(fillPictureSlot),
  scenePictureOperation(setPictureFraming),
  scenePictureOperation(clearPictures),
  scenePictureOperation(resetPictureSlot),
  scenePictureOperation(setPicturePlayback),
  scenePictureOperation(setPictureKind),
]);

/** The picture tools the scene assistant is offered (D-15): never framing, discard or reset. */
export const SCENE_AGENT_PICTURE_TOOLS: readonly string[] = Object.freeze(['notePictureSlot', 'removePictureSlot', 'fillPictureSlot']);

/** The scene's "Pictures:" section in `describeStory`'s line format, or `''` when its map has no slot. */
export function describeScenePictures(doc: PictureSceneDoc): string {
  return picturesSection(scenePictureMapOf(doc), [scenePictureSceneOf(doc)]).join('\n');
}
